import type { AxisProfile, CeilingNode, CeilingProfile } from '@roomtreatment/core';

export type { AxisProfile, CeilingNode, CeilingProfile };
import {
  CAD_VIEW_IDENTITY,
  cadViewToScreen,
  screenToCadView,
  isPointInView,
  type CadView2D,
  type CadScaleBounds,
} from './cadView';
import type { PolyPt } from './polygon';

export type CeilingAxis = 'x' | 'y';

export interface RoofPreset {
  key: string;
  label: string;
}

export const ROOF_PRESETS: RoofPreset[] = [
  { key: 'flat', label: 'Flat' },
  { key: 'shed', label: 'Shed' },
  { key: 'vault', label: 'Vault' },
  { key: 'cathedral', label: 'Cathedral' },
  { key: 'barrel', label: 'Barrel' },
];

const PRESET_NODES: Record<string, { nodes: CeilingNode[]; mode?: 'linear' | 'smooth' } | null> = {
  flat: null,
  shed: { nodes: [[0.0, 2.3], [1.0, 3.2]] },
  vault: { nodes: [[0.0, 2.5], [0.5, 3.6], [1.0, 2.5]], mode: 'smooth' },
  cathedral: { nodes: [[0.0, 2.3], [0.5, 4.0], [1.0, 2.3]] },
  barrel: { nodes: [[0.0, 2.4], [0.2, 3.0], [0.5, 3.2], [0.8, 3.0], [1.0, 2.4]], mode: 'smooth' },
};

export function axisProfileFlat(ap: AxisProfile | null | undefined): boolean {
  if (!ap || !ap.nodes || ap.nodes.length < 2) return true;
  const z0 = ap.nodes[0][1];
  return ap.nodes.every((n) => Math.abs(n[1] - z0) <= 1e-6);
}

/** Lowest a node may sit — just clear of the floor plane, so a profile can
 *  never invert through it. There is deliberately no matching maximum; see
 *  {@link ceilingZRange}. */
export const MIN_CEILING_NODE_M = 0.01;

/** The default frame always reaches at least this high, so even a low room
 *  opens with real headroom to drag up into. This is the number that used to
 *  double as the editor's height cap; as a framing floor it is doing the one
 *  job it was ever good at. */
const MIN_FRAME_TOP_M = 4.5;

/** How far above the tallest node the frame reaches once it has to grow to
 *  fit one: enough daylight that a node parked at the profile's peak still
 *  has a grabbable handle and a readable dimension label above it, and
 *  enough that a drag-to-the-top-then-release cycle buys real room to climb
 *  further. */
const FRAME_HEADROOM = 1.15;

/** Top of the frame for a profile whose tallest node is `peak`. A profile
 *  that already fits inside the default frame leaves it EXACTLY alone —
 *  every roof preset this app ships does, so none of them look any
 *  different for the frame having learned to grow. */
function frameTop(base: number, peak: number): number {
  return peak <= base ? base : peak * FRAME_HEADROOM;
}

/** The frame the profile is drawn in — floor to the top of the drawing, in
 *  metres. NOT a limit on how tall a node may be.
 *
 *  It used to be both, which is exactly where the editor's 4.5m height cap
 *  came from: `ceilingPxToNode` clamps a pointer into the frame, and both
 *  typed-height paths clamped to it as well, so the top of the drawing
 *  quietly became the tallest ceiling anyone could draw. Nothing physical
 *  justified it — an A-frame or cathedral peak clears 4.5m easily — and a
 *  room of ordinary height pinned all three terms below at once, so the cap
 *  never moved off 4.5 no matter what was being drawn.
 *
 *  The two jobs are now split. Node height is bounded below by the floor
 *  ({@link MIN_CEILING_NODE_M}) and unbounded above; this function is the
 *  framing decision alone — at least the default headroom over the room, and
 *  always tall enough to show the profile it was handed. Pass `nodes` to fit
 *  the frame to real content; omit them for the bare default.
 *
 *  Fitting to content makes the frame content-DEPENDENT, which a drag has to
 *  be insulated from — see `CeilingProfileCanvas.tsx`, which snapshots the
 *  frame for the duration of a node gesture. A caller that drags nodes
 *  against a live-refitting frame will watch them chase the pointer. */
export function ceilingZRange(
  heightM: number,
  nodes?: readonly CeilingNode[],
): [number, number] {
  const base = Math.max(heightM * 1.6, heightM + 1.5, MIN_FRAME_TOP_M);
  let peak = 0;
  for (const n of nodes ?? []) if (n[1] > peak) peak = n[1];
  return [MIN_CEILING_NODE_M, frameTop(base, peak)];
}

export function resolvePrimaryAxis(profile: CeilingProfile | null): CeilingAxis {
  if (!profile) return 'x';
  const xA = !!(profile.x && !axisProfileFlat(profile.x));
  const yA = !!(profile.y && !axisProfileFlat(profile.y));
  if (xA && !yA) return 'x';
  if (yA && !xA) return 'y';
  return profile.primary_axis || 'x';
}

/** An axis folds away to nothing only when it carries no structure beyond
 *  the implicit default (at most the two endpoint nodes, flat) — a
 *  3+-node axis that happens to be geometrically flat RIGHT NOW (mid-drag
 *  passing through a neighbour's height, or a 5cm-snapped value landing on
 *  it) still carries real shape in its interior nodes' t-positions. Folding
 *  it into `null` here would silently discard that shape the instant the
 *  heights coincide, not just when the user actually asks for a flat roof
 *  — the exact bug `moveCeilingNode`'s height-snap surfaced: a drag that
 *  passes back through the default height mid-gesture used to wipe the
 *  node the user was actively dragging. `axisProfileFlat` itself is left
 *  alone — `resolvePrimaryAxis`/`matchedPreset` still want "all heights
 *  equal" to mean flat regardless of node count. */
function axisIsBareDefault(ap: AxisProfile | null | undefined): boolean {
  if (!ap || !ap.nodes || ap.nodes.length <= 2) return axisProfileFlat(ap);
  return false;
}

export function normalizeCeiling(profile: CeilingProfile | null): CeilingProfile | null {
  if (!profile) return null;
  const xBare = axisIsBareDefault(profile.x);
  const yBare = axisIsBareDefault(profile.y);
  if (xBare && yBare) return null;
  return { ...profile, primary_axis: resolvePrimaryAxis(profile) };
}

export function applyCeilingPreset(
  profile: CeilingProfile | null,
  axis: CeilingAxis,
  name: string,
): CeilingProfile | null {
  if (name === 'flat') {
    if (!profile) return null;
    const next: CeilingProfile = { ...profile, [axis]: null } as CeilingProfile;
    return normalizeCeiling(next);
  }
  const p = PRESET_NODES[name];
  if (!p) return profile;
  const base: CeilingProfile = profile
    ? { ...profile }
    : { x: null, y: null, primary_axis: 'x' };
  base[axis] = {
    nodes: p.nodes.map((n) => [n[0], n[1]] as CeilingNode),
    mode: p.mode || 'linear',
  };
  return normalizeCeiling(base);
}

export function matchedPreset(profile: CeilingProfile | null, axis: CeilingAxis): string {
  const ap = profile ? profile[axis] : null;
  if (!ap || axisProfileFlat(ap)) return 'flat';
  const key = JSON.stringify(
    ap.nodes.map((n) => [Math.round(n[0] * 100) / 100, Math.round(n[1] * 10) / 10]),
  );
  for (const name of Object.keys(PRESET_NODES)) {
    const p = PRESET_NODES[name];
    if (!p) continue;
    const k2 = JSON.stringify(
      p.nodes.map((n) => [Math.round(n[0] * 100) / 100, Math.round(n[1] * 10) / 10]),
    );
    if (k2 === key) return name;
  }
  return '';
}

export function activeCeilingNodes(
  profile: CeilingProfile | null,
  axis: CeilingAxis,
  heightM: number,
): CeilingNode[] {
  const ap = profile ? profile[axis] : null;
  if (ap && ap.nodes && ap.nodes.length >= 2) return ap.nodes;
  return [[0, heightM], [1, heightM]];
}

export function setAxisNodes(
  profile: CeilingProfile | null,
  axis: CeilingAxis,
  nodes: CeilingNode[],
  mode?: 'linear' | 'smooth',
): CeilingProfile | null {
  const base: CeilingProfile = profile
    ? { ...profile }
    : { x: null, y: null, primary_axis: 'x' };
  const prevMode = (profile && profile[axis]?.mode) || 'linear';
  base[axis] = {
    nodes: nodes.map((n) => [n[0], n[1]] as CeilingNode),
    mode: mode || prevMode,
  };
  return normalizeCeiling(base);
}

export interface CeilingViewGeom {
  padX: number;
  padY: number;
  width: number;
  height: number;
  zLo: number;
  zHi: number;
}

/** `nodes` is the set the frame is FITTED to, which during a drag is not
 *  necessarily the set being drawn — see `ceilingZRange`. */
export function ceilingViewGeom(
  width: number,
  height: number,
  heightM: number,
  nodes?: readonly CeilingNode[],
): CeilingViewGeom {
  const [zLo, zHi] = ceilingZRange(heightM, nodes);
  return { padX: 14, padY: 6, width, height, zLo, zHi };
}

export function ceilingNodeToPx(geom: CeilingViewGeom, n: CeilingNode): { x: number; y: number } {
  const { padX, padY, width, height, zLo, zHi } = geom;
  return {
    x: padX + n[0] * (width - 2 * padX),
    y: height - padY - ((n[1] - zLo) / (zHi - zLo)) * (height - 2 * padY - 6),
  };
}

export function ceilingPxToNode(
  geom: CeilingViewGeom,
  x: number,
  y: number,
): CeilingNode {
  const { padX, padY, width, height, zLo, zHi } = geom;
  const t = Math.max(0, Math.min(1, (x - padX) / Math.max(1, width - 2 * padX)));
  const zFrac = (height - padY - y) / Math.max(1, height - 2 * padY - 6);
  const z = zLo + Math.max(0, Math.min(1, zFrac)) * (zHi - zLo);
  return [t, z];
}

export function hitTestCeilingNode(
  geom: CeilingViewGeom,
  nodes: CeilingNode[],
  x: number,
  y: number,
  radiusPx = 18,
): number {
  let best = -1;
  let bestD = radiusPx;
  nodes.forEach((n, i) => {
    const p = ceilingNodeToPx(geom, n);
    const d = Math.hypot(p.x - x, p.y - y);
    if (d <= bestD) {
      bestD = d;
      best = i;
    }
  });
  return best;
}

/** Move a node; endpoints keep t locked at 0/1. */
export function moveCeilingNode(
  nodes: CeilingNode[],
  index: number,
  next: CeilingNode,
): CeilingNode[] {
  if (index < 0 || index >= nodes.length) return nodes;
  const out = nodes.map((n) => [n[0], n[1]] as CeilingNode);
  const t =
    index === 0 ? 0
      : index === nodes.length - 1 ? 1
        : Math.max(out[index - 1][0] + 0.02, Math.min(out[index + 1][0] - 0.02, next[0]));
  out[index] = [t, Math.max(MIN_CEILING_NODE_M, next[1])];
  return out;
}

export function addCeilingNode(nodes: CeilingNode[], at: CeilingNode): CeilingNode[] {
  const t = Math.max(0.05, Math.min(0.95, at[0]));
  const out = nodes.map((n) => [n[0], n[1]] as CeilingNode);
  let i = 1;
  while (i < out.length - 1 && out[i][0] < t) i += 1;
  out.splice(i, 0, [t, at[1]]);
  return out;
}

export function removeCeilingNode(nodes: CeilingNode[], index: number): CeilingNode[] {
  if (index <= 0 || index >= nodes.length - 1 || nodes.length <= 2) return nodes;
  return nodes.filter((_, i) => i !== index);
}

// ── View: pan/zoom on top of the fit geometry ───────────────────────────
//
// `ceilingViewGeom` above is the auto-fit frame, with no notion of the user
// having panned or zoomed. Rather than fold pan/zoom into it (which mobile
// and every other `ceilingViewGeom`/`ceilingNodeToPx`/`ceilingPxToNode`
// caller would then have to carry around), `CeilingView` is a small affine
// transform layered ON TOP of that fit space: `{scale: 1, originX: 0,
// originY: 0}` is exactly the un-panned, un-zoomed picture, so every caller
// that never asks for a camera (mobile's ceiling-profile-editor, in
// particular) is unaffected by this existing at all.
//
// This mirrors `view.ts`'s `FloorplanView`/`zoomViewAt`/`panView`. It is
// only the CAMERA that a node edit leaves untouched, though: the fit space
// underneath it grows in z to keep a tall profile on screen (`ceilingZRange`,
// once it is handed nodes), so the frame a node is measured in IS
// content-dependent and a drag has to be insulated from it the same way
// `RoomFloorplanCanvas` insulates itself — see `CeilingProfileCanvas`, which
// snapshots the frame for the duration of a node gesture. A `CeilingView`
// itself still persists untouched across every node edit; it changes only
// from an explicit wheel-zoom, drag-to-pan, or Fit.
/** The ceiling's camera is the shared CAD camera (`cadView.ts`) — the same
 *  affine every 2D editor pans/zooms with — layered on top of the fit
 *  geometry above. Only the names and the scale bounds are ceiling-domain:
 *  the fit already frames the whole profile, so zoom is a multiple of fit
 *  (0.5x..8x), not the room's absolute px-per-metre range. */
export type CeilingView = CadView2D;

/** The un-panned, un-zoomed identity transform — what "Fit" resets to. */
export const CEILING_VIEW_FIT: CeilingView = CAD_VIEW_IDENTITY;

export const CEILING_VIEW_SCALE_BOUNDS: CadScaleBounds = { min: 0.5, max: 8 };
export const MIN_CEILING_VIEW_SCALE = CEILING_VIEW_SCALE_BOUNDS.min;
export const MAX_CEILING_VIEW_SCALE = CEILING_VIEW_SCALE_BOUNDS.max;

export const ceilingFitToScreen = cadViewToScreen;
export const ceilingScreenToFit = screenToCadView;

/** Where a node draws on screen once the view's pan/zoom is applied on top
 *  of the fit geometry — what both the Canvas2D renderer and the DOM
 *  dimension-label overlay position from, so the label can never drift off
 *  the node it names. */
export function ceilingNodeToScreen(
  geom: CeilingViewGeom,
  view: CeilingView,
  n: CeilingNode,
): { x: number; y: number } {
  return ceilingFitToScreen(view, ceilingNodeToPx(geom, n));
}

/** The inverse of {@link ceilingNodeToScreen} — where a screen point (a
 *  pointer position) lands as a `[t, z]` node, after undoing the view's
 *  pan/zoom. `ceilingPxToNode` already clamps both `t` and `z` into their
 *  sane ranges, so this stays safe even for a screen point far outside the
 *  canvas (a fast drag under pointer capture routinely reports one). */
export function ceilingScreenToNode(
  geom: CeilingViewGeom,
  view: CeilingView,
  screen: { x: number; y: number },
): CeilingNode {
  const fit = ceilingScreenToFit(view, screen);
  return ceilingPxToNode(geom, fit.x, fit.y);
}

/** Screen-space vertical dimension annotation for one profile node — the
 *  ceiling counterpart of `computeEdgeDimensions` in `floorPolygon.ts`. A
 *  wall's annotation runs along the wall itself, offset sideways; a node's
 *  height annotation runs from the floor straight up to the node, offset
 *  sideways instead, so the shape is the same `{aPx, bPx, normal, dimA,
 *  dimB}` `drawDimAnnotation` already knows how to draw — just built from a
 *  vertical `a->b` instead of a horizontal one. */
export interface CeilingHeightDimension {
  index: number;
  heightM: number;
  /** Screen-space floor point directly below the node. */
  aPx: PolyPt;
  /** Screen-space node position. */
  bPx: PolyPt;
  /** Screen-space unit normal — always horizontal, since `aPx`/`bPx` are
   *  always vertically stacked. Points toward the canvas's horizontal centre
   *  so an endpoint node's annotation offsets inward, never off the edge of
   *  the frame it's drawn in. */
  normal: PolyPt;
  dimA: PolyPt;
  dimB: PolyPt;
  /** Screen-space midpoint of the dimension line, RAW and unclamped — same
   *  convention as `EdgeDimension.labelPos`: where the label belongs when it
   *  belongs on screen at all, with `visible` below gating whether it does. */
  labelPos: PolyPt;
  visible: boolean;
}

export function computeCeilingHeightDimensions(
  nodes: CeilingNode[],
  geom: CeilingViewGeom,
  view: CeilingView,
  offsetPx = 26,
): CeilingHeightDimension[] {
  return nodes.map((n, i) => {
    const aPx = ceilingNodeToScreen(geom, view, [n[0], geom.zLo]);
    const bPx = ceilingNodeToScreen(geom, view, n);
    // Left half of the profile offsets right, right half offsets left —
    // every annotation points toward centre, so the two endpoint nodes
    // (sitting right at the frame's left/right edge) never get clipped.
    const nx = n[0] < 0.5 ? 1 : -1;
    const normal: PolyPt = { x: nx, y: 0 };
    const dimA = { x: aPx.x + normal.x * offsetPx, y: aPx.y };
    const dimB = { x: bPx.x + normal.x * offsetPx, y: bPx.y };
    const labelPos = { x: dimA.x, y: (dimA.y + dimB.y) / 2 };
    return {
      index: i,
      heightM: n[1],
      aPx,
      bPx,
      normal,
      dimA,
      dimB,
      labelPos,
      visible: isPointInView(labelPos, { width: geom.width, height: geom.height }),
    };
  });
}
